// VOY-485 (rebuild) — Account (Settings tab).
//
// Personal fields: full name (split first / last via inline edit), email
// (auth-owned, read-only), phone, location, DOB. Editable fields wire to
// `updateProfile()`. Location is not yet a column on `profiles`; we render
// the chrome with a placeholder so the row is visible — TODO to add when
// the schema lands.
//
// SENSITIVE DATA RULES (hard requirement from the ticket):
//   - DOB rendered MASKED. The user types it in themselves; we never
//     pre-fill, auto-complete, or transmit a fragment we didn't get from
//     the user this session. Never log full DOB values anywhere
//     (no console, analytics, error payloads).

import { useState, type ChangeEvent } from 'react'
import { updateProfile } from '@/lib/profile'
import type { Profile } from '@/types/profile'

export interface AccountSectionProps {
  profile: Profile
  onProfileChanged?: () => void
}

function maskedDob(birthDate: string | null | undefined): string {
  return birthDate ? '•••• ••, ••••' : ''
}

type Field = 'name' | 'last_name' | 'phone' | 'birth_date'

export function AccountSection({ profile, onProfileChanged }: AccountSectionProps) {
  const [editing, setEditing] = useState<Field | null>(null)
  const [draft, setDraft] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function beginEdit(field: Field, current: string | null | undefined) {
    setEditing(field)
    // SENSITIVE DATA: never pre-fill DOB. The user re-enters it if they
    // want to change it. Same pattern as TravelCompanionsSection. The
    // masked display is only used in read mode; clicking the field
    // opens an empty input — never the raw value.
    setDraft(field === 'birth_date' ? '' : (current ?? ''))
    setError(null)
  }

  async function commit() {
    if (!editing) return
    setSaving(true)
    try {
      const trimmed = draft.trim()
      const patch: Partial<Profile> = { [editing]: trimmed === '' ? null : trimmed }
      await updateProfile(profile.id, patch)
      setEditing(null)
      onProfileChanged?.()
    } catch (err) {
      // Important: never echo the user-entered value into the error message,
      // especially for birth_date.
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  function renderEditable(field: Field, label: string, current: string | null | undefined, mask?: (v: string | null | undefined) => string) {
    const displayValue = mask ? mask(current) : (current ?? '')
    if (editing === field) {
      return (
        <div className="field" key={field}>
          <span className="field-l">{label}</span>
          <input
            autoFocus
            // DOB-specific behaviour: empty input, never the stored value.
            placeholder={field === 'birth_date' ? 'YYYY-MM-DD (leave blank to keep)' : undefined}
            autoComplete={field === 'birth_date' ? 'off' : undefined}
            spellCheck={false}
            value={draft}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
            onBlur={() => {
              // For DOB: blank submission means "keep current value", not
              // "clear DOB". Cancel the edit instead of writing a null.
              if (field === 'birth_date' && draft.trim() === '') {
                setEditing(null)
                return
              }
              void commit()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (field === 'birth_date' && draft.trim() === '') {
                  setEditing(null)
                  return
                }
                void commit()
              } else if (e.key === 'Escape') setEditing(null)
            }}
            disabled={saving}
          />
        </div>
      )
    }
    return (
      <button
        type="button"
        key={field}
        className="field"
        onClick={() => beginEdit(field, current)}
        style={{ textAlign: 'left', cursor: 'text' }}
        title={`Click to edit ${label.toLowerCase()}`}
      >
        <span className="field-l">{label}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: displayValue ? 'var(--v6-fg-strong)' : 'var(--v6-fg-muted)' }}>
          {displayValue || `Add ${label.toLowerCase()}`}
        </span>
      </button>
    )
  }

  return (
    <section className="card" aria-labelledby="account-h">
      <div className="card-h">
        <h3 id="account-h">Account</h3>
        <span className="meta">PERSONAL</span>
      </div>
      <div className="stack">
        <div className="row" style={{ gap: 12 }}>
          <div style={{ flex: 1 }}>{renderEditable('name', 'FIRST NAME', profile.name)}</div>
          <div style={{ flex: 1 }}>{renderEditable('last_name', 'LAST NAME', profile.last_name)}</div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <span className="field-l">EMAIL</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--v6-fg-muted)' }}>
              {profile.email || '—'}
            </span>
          </div>
          <div style={{ flex: 1 }}>{renderEditable('phone', 'PHONE', profile.phone)}</div>
        </div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <span className="field-l">LOCATION</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--v6-fg-muted)' }}>
              Set on your next booking
            </span>
          </div>
          <div style={{ flex: 1 }}>{renderEditable('birth_date', 'DATE OF BIRTH', profile.birth_date, maskedDob)}</div>
        </div>
        {error && <p style={{ color: 'var(--v6-neg)', fontSize: 12, margin: 0 }} role="alert">{error}</p>}
      </div>
    </section>
  )
}
